import React, {useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useQuery} from "react-query";
import ngage from "services/ngage";
import Page from 'components/Page';
import Card from "components/Card";
import DescriptionList from "components/DescriptionList";
import DescriptionTerm from "components/DescriptionTerm";
import DescriptionDetails from "components/DescriptionDetails";
import DescriptionListItem from "components/DescriptionListItem";
import {Article} from "types/Article";
import Chip from "components/Chip";
import Button from "components/Button";
import {PencilIcon, TrashIcon} from "@heroicons/react/solid";
import DeleteArticle from '../Delete';
import ArticleStatus from "types/ArticleStatus";
import DotBadge from "components/DotBadge";
import {formattedDateTime} from "services/date";
import {toTitleCase} from 'services/string';
import BooleanIndicator from "components/BooleanIndicator";
import IconBadge from "components/IconBadge";
import IconName from "types/IconName";
import Gallery from "../../../components/Gallery";

// Map message status to badge color
const statusColors: Record<ArticleStatus, number> = {
  [ArticleStatus.UNPUBLISHED]: DotBadge.colors.GRAY,
  [ArticleStatus.SCHEDULED]: DotBadge.colors.YELLOW,
  [ArticleStatus.PUBLISHED]: DotBadge.colors.GREEN,
}

function linkChipHref(url: string): string {
  if(!url.startsWith('ngage')) return url;

  const path = url.split('://')[1]

  return '/' + path.split('/')[0];
}

function ViewArticle() {
  const {id} = useParams()
  const {data: article, isLoading} = useQuery(
    ['articles', {id}],
    () => ngage.articles.get(parseInt(id as string))
  )

  const [deletingArticle, setDeletingArticle] = useState<Article | null>(null);

  const navigate = useNavigate()

  // Prepare action handlers
  function handleDeleteArticle() {
    if (article) setDeletingArticle(article)
  }

  function handleEditArticle() {
    if (id) navigate(`/articles/${id}/edit`)
  }

  // Prepare breadcrumbs, metadata and actions
  const breadcrumbs = {"Content": "/", "News": "/articles", "Article": `/articles/${id}`}
  const actions = (<>
    <Button
      color={Button.colors.WHITE}
      icon={TrashIcon}
      onClick={handleDeleteArticle}
      disabled={!article}
    >
      Delete
    </Button>
    <Button
      color={Button.colors.PRIMARY}
      icon={PencilIcon}
      onClick={handleEditArticle}
      disabled={!article}
    >
      Edit
    </Button>
  </>)

  return (
    <Page title={article?.title || "View Article"} breadcrumbs={breadcrumbs} actions={actions} isLoading={isLoading}>
      {article &&
        <>
          {/* Article Information */}
          <section className="flex flex-col lg:flex-row-reverse lg:justify-end">
            {article.image &&
              <div
                className="flex justify-center items-center max-h-36 lg:max-h-full shadow overflow-hidden sm:rounded-md sm:mb-2 lg:mb-0 lg:ml-4">
                <img
                  src={article.image.preview_url}
                  alt={article.title}
                  className="object-cover w-full lg:h-full lg:w-auto"
                />
              </div>
            }

            <div className="lg:basis-2/3 lg:flex-shrink-0 lg:flex-grow">
              <Card
                heading="Article Details"
                subheading={article.is_external ? 'External article' : 'Internal article'}
              >
                <DescriptionList>
                  <DescriptionListItem span={1}>
                    <DescriptionTerm>Status</DescriptionTerm>
                    <DescriptionDetails>
                      <DotBadge color={statusColors[article.status]} text={toTitleCase(article.status)}/>
                    </DescriptionDetails>
                  </DescriptionListItem>

                  <DescriptionListItem span={1}>
                    <DescriptionTerm>Publish Date</DescriptionTerm>
                    <DescriptionDetails>
                      {formattedDateTime(article.published_at) ?? 'Never'}
                    </DescriptionDetails>
                  </DescriptionListItem>

                  <DescriptionListItem span={1}>
                    <DescriptionTerm>Featured</DescriptionTerm>
                    <DescriptionDetails>
                      <BooleanIndicator value={article.is_featured}/>
                    </DescriptionDetails>
                  </DescriptionListItem>

                  <DescriptionListItem span={2}>
                    <DescriptionTerm>Excerpt</DescriptionTerm>
                    <DescriptionDetails>
                      <p className="whitespace-pre-line">{article.excerpt || 'N/A'}</p>
                    </DescriptionDetails>
                  </DescriptionListItem>

                  <DescriptionListItem span={2}>
                    <DescriptionTerm>Tags</DescriptionTerm>
                    <DescriptionDetails>
                      {article.tags.length < 1 &&
                        <span>None</span>
                      }

                      {article.tags.map(tag => (
                        <IconBadge
                          key={tag.id}
                          color={IconBadge.colors.BRAND}
                          size={IconBadge.sizes.LARGE}
                          icon={IconName.TAG}
                          text={tag.name}
                        />
                      ))}
                    </DescriptionDetails>
                  </DescriptionListItem>

                  {article.is_external &&
                    <DescriptionListItem span={1}>
                      <DescriptionTerm>Source</DescriptionTerm>
                      <DescriptionDetails>
                        <Chip name={article.source.name} href={article.source.url}/>
                      </DescriptionDetails>
                    </DescriptionListItem>
                  }

                  <DescriptionListItem span={2}>
                    <DescriptionTerm>Sites</DescriptionTerm>
                    <DescriptionDetails>
                      {article.sites.length < 1 &&
                        <span>All</span>
                      }

                      {article.sites.map(site => (
                        <IconBadge
                          key={site.id}
                          color={IconBadge.colors.BRAND}
                          size={IconBadge.sizes.LARGE}
                          icon={IconName.OFFICE_BUILDING}
                          text={site.name}
                        />
                      ))}
                    </DescriptionDetails>
                  </DescriptionListItem>
                </DescriptionList>
              </Card>
            </div>
          </section>

          {/* Content */}
          {!article.is_external &&
            <>

              {/* Body */}
              <section className="mt-4">
                <Card
                  heading={article.title}
                  subheading={article.author ? `Written by ${article.author.name}` : null}
                >
                  <p className="whitespace-pre-line">
                    {article.body}
                  </p>
                </Card>
              </section>


              {/* Media */}
              {!!article.media.length &&
                <section className="mt-4">
                  <Card heading="Media">
                    <Gallery media={article.media}/>
                  </Card>
                </section>
              }

              {/* Links */}
              <section className="mt-4">
                <Card heading={'Links'}>
                  <ul className="sm:grid sm:grid-cols-2 gap-2">
                    {article.links.map(link => (
                      <li key={link.id}>
                        <Chip name={link.text} href={linkChipHref(link.url)}/>
                      </li>
                    ))}
                  </ul>
                </Card>
              </section>
            </>
          }
        </>
      }

      {/* Delete confirmation modal */}
      <DeleteArticle
        show={article === deletingArticle}
        article={deletingArticle}
        onCancel={() => setDeletingArticle(null)}
        onDelete={() => navigate('/articles')}
        onDeleteFail={() => setDeletingArticle(null)}
      />
    </Page>
  );
}

export default ViewArticle;